import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import {
  BellRing,
  Package,
  Edit2,
  Trash2,
  AlertCircle,
  List,
  Plus,
  Tag,
  MessageSquare,
  Shield,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import TwoFactorAuth from "./TwoFactorAuth";
import {
  isGlobalTwoFactorRequired,
  setGlobalTwoFactorRequired as setGlobalTwoFactorRequiredAPI,
} from "./enforceTwoFactor";
import { db, auth, ensureAuthenticated } from "./firebase";

function Settings() {
  // Product Management State
  const [products, setProducts] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [productCount, setProductCount] = useState(0);
  const [newProduct, setNewProduct] = useState({
    name: "",
    price: "",
    logo: "",
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [error, setError] = useState(null);

  // Product Variants State
  const [selectedProduct, setSelectedProduct] = useState("Autodesk");
  const [productVariants, setProductVariants] = useState([]);
  const [newVariant, setNewVariant] = useState("");
  const [editingVariant, setEditingVariant] = useState(null);
  const [deletingVariant, setDeletingVariant] = useState(null);
  const [variantError, setVariantError] = useState(null);
  const [variantSuccess, setVariantSuccess] = useState(null);

  // Renewal Settings State
  const [renewalDays, setRenewalDays] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [renewalSuccess, setRenewalSuccess] = useState(null);
  const [renewalError, setRenewalError] = useState(null);

  // Renewal Price Settings State
  const [renewalPrices, setRenewalPrices] = useState({});
  const [selectedRenewalProduct, setSelectedRenewalProduct] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState("");
  const [renewalPriceLoading, setRenewalPriceLoading] = useState(false);
  const [renewalPriceSuccess, setRenewalPriceSuccess] = useState(null);
  const [renewalPriceError, setRenewalPriceError] = useState(null);

  // Renewal Message Template State
  const [renewalMessage, setRenewalMessage] = useState("");
  const [renewalMessageLoading, setRenewalMessageLoading] = useState(false);
  const [renewalMessageSuccess, setRenewalMessageSuccess] = useState(null);
  const [renewalMessageError, setRenewalMessageError] = useState(null);

  // Expire Message Template State
  const [expireMessage, setExpireMessage] = useState("");
  const [expireMessageLoading, setExpireMessageLoading] = useState(false);
  const [expireMessageSuccess, setExpireMessageSuccess] = useState(null);
  const [expireMessageError, setExpireMessageError] = useState(null);

  // Messaging Settings State
  const [facebookBusinessUrl, setFacebookBusinessUrl] = useState("");
  const [messagingSettingsLoading, setMessagingSettingsLoading] =
    useState(false);
  // eslint-disable-next-line no-unused-vars
  const [messagingSettingsSuccess, setMessagingSettingsSuccess] =
    useState(null);
  // eslint-disable-next-line no-unused-vars
  const [messagingSettingsError, setMessagingSettingsError] = useState(null);

  // Two-Factor Authentication State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorSuccess, setTwoFactorSuccess] = useState(null);
  const [twoFactorError, setTwoFactorError] = useState(null);
  const [globalTwoFactorRequired, setGlobalTwoFactorRequiredState] =
    useState(false);

  // Add a state to track if user has permission to manage global settings
  const [canManageGlobalSettings, setCanManageGlobalSettings] = useState(true);

  // Collapsible sections — all collapsed by default
  const [openSections, setOpenSections] = useState({});
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    const initializeSettings = async () => {
      try {
        // Check if user is authenticated
        const currentUser = auth.currentUser;
        console.log(
          "Initial auth state:",
          currentUser ? "Authenticated" : "Not authenticated"
        );

        if (!currentUser) {
          console.log(
            "No user authenticated, attempting to ensure authentication"
          );
          try {
            await ensureAuthenticated();
            console.log("Authentication ensured");
          } catch (authError) {
            console.error("Failed to ensure authentication:", authError);
          }
        }

        // Fetch data after authentication check
        fetchProducts();
        fetchRenewalDays();
        fetchProductVariants(selectedProduct);
        fetchRenewalPrices();
        fetchRenewalMessage();
        fetchExpireMessage();
        fetchMessagingSettings();

        // Fetch 2FA status last to ensure authentication is ready
        console.log(
          "Fetching 2FA status with auth state:",
          auth.currentUser ? "Authenticated" : "Not authenticated"
        );
        await fetchTwoFactorStatus();
        await fetchGlobalTwoFactorStatus();
      } catch (error) {
        console.error("Error initializing settings:", error);
      }
    };

    initializeSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch product variants when selected product changes
  useEffect(() => {
    fetchProductVariants(selectedProduct);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  // Update price fields when selected renewal product changes
  useEffect(() => {
    if (selectedRenewalProduct && renewalPrices[selectedRenewalProduct]) {
      setCurrentPrice(renewalPrices[selectedRenewalProduct].currentPrice || "");
      setDiscountedPrice(
        renewalPrices[selectedRenewalProduct].discountedPrice || ""
      );
    } else {
      // Find the product's regular price to set as default current price
      const product = products.find((p) => p.name === selectedRenewalProduct);
      setCurrentPrice(product?.price || "");
      // Default discounted price is 90% of current price
      setDiscountedPrice(product?.price ? Math.round(product.price * 0.9) : "");
    }
  }, [selectedRenewalProduct, renewalPrices, products]);

  // Product Management Functions
  const fetchProducts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "Products"));
      const productList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productList);
      setProductCount(productList.length);

      // Set default selected renewal product if not already set
      if (productList.length > 0 && !selectedRenewalProduct) {
        setSelectedRenewalProduct(productList[0].name);
      }
    } catch (error) {
      console.error("Error fetching products: ", error);
      setError("Failed to fetch products. Please try again.");
    }
  };

  const handleInputChange = (e, isNewProduct = true) => {
    const { name, value } = e.target;
    if (isNewProduct) {
      setNewProduct((prev) => ({ ...prev, [name]: value }));
    } else {
      setEditingProduct((prev) => ({ ...prev, [name]: value }));
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const productName = newProduct.name.trim();
      if (productName === "") {
        throw new Error("Product name cannot be empty");
      }

      const existingProducts = products.find(
        (p) => p.name.toLowerCase() === productName.toLowerCase()
      );

      if (existingProducts) {
        throw new Error("A product with this name already exists");
      }

      await setDoc(doc(db, "Products", productName), {
        name: productName,
        price: newProduct.price,
        logo: newProduct.logo,
      });

      setNewProduct({ name: "", price: "", logo: "" });
      fetchProducts();
    } catch (error) {
      console.error("Error adding product: ", error);
      setError(error.message);
    }
  };

  const startEditing = (product) => {
    setEditingProduct(product);
  };

  const saveEdit = async () => {
    setError(null);
    try {
      if (editingProduct.name !== editingProduct.id) {
        await setDoc(doc(db, "Products", editingProduct.name), {
          name: editingProduct.name,
          price: editingProduct.price,
          logo: editingProduct.logo,
        });
        await deleteDoc(doc(db, "Products", editingProduct.id));
      } else {
        await updateDoc(doc(db, "Products", editingProduct.id), {
          name: editingProduct.name,
          price: editingProduct.price,
          logo: editingProduct.logo,
        });
      }
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error("Error updating product: ", error);
      setError(error.message);
    }
  };

  const confirmDelete = (product) => {
    setDeletingProduct(product);
  };

  const deleteProduct = async () => {
    setError(null);
    try {
      await deleteDoc(doc(db, "Products", deletingProduct.id));
      setDeletingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product: ", error);
      setError(error.message);
    }
  };

  // Product Variants Functions
  const fetchProductVariants = async (productName) => {
    try {
      setVariantError(null);

      // First, check if the ProductVariants collection exists
      try {
        // Try to get all documents from ProductVariants collection
        const variantsRef = collection(db, "ProductVariants");
        const q = query(variantsRef, where("productName", "==", productName));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Get the first document that matches
          const variantDoc = querySnapshot.docs[0];
          const variantData = variantDoc.data();
          setProductVariants(variantData.variants || []);
          return;
        }
      } catch (collectionError) {
        console.log(
          "Collection might not exist, creating it:",
          collectionError
        );
        // The collection might not exist, we'll create it below
      }

      // Create a new document if none exists or collection doesn't exist
      // We'll create the collection implicitly by creating a document
      const newVariantDoc = doc(collection(db, "ProductVariants"));
      await setDoc(newVariantDoc, {
        productName: productName,
        variants: [],
        createdAt: new Date().toISOString(),
      });

      setProductVariants([]);
    } catch (error) {
      console.error("Error fetching product variants:", error);
      setVariantError("Failed to fetch product variants");
    }
  };

  const handleNewVariantChange = (e) => {
    setNewVariant(e.target.value);
  };

  const addVariant = async () => {
    try {
      setVariantError(null);
      setVariantSuccess(null);

      if (!newVariant.trim()) {
        setVariantError("Variant name cannot be empty");
        return;
      }

      // Check if variant already exists
      if (productVariants.includes(newVariant.trim())) {
        setVariantError("This variant already exists");
        return;
      }

      const updatedVariants = [...productVariants, newVariant.trim()];

      // Try to find existing document first
      const variantsRef = collection(db, "ProductVariants");
      const q = query(variantsRef, where("productName", "==", selectedProduct));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Update existing document
        const variantDoc = querySnapshot.docs[0];
        await updateDoc(variantDoc.ref, {
          variants: updatedVariants,
          updatedAt: new Date().toISOString(),
        });
      } else {
        // Create new document
        const newDocRef = doc(collection(db, "ProductVariants"));
        await setDoc(newDocRef, {
          productName: selectedProduct,
          variants: updatedVariants,
          createdAt: new Date().toISOString(),
        });
      }

      setProductVariants(updatedVariants);
      setNewVariant("");
      setVariantSuccess("Variant added successfully!");
    } catch (error) {
      console.error("Error adding variant:", error);
      setVariantError(error.message || "Failed to add variant");
    }
  };

  const startEditingVariant = (variant, index) => {
    setEditingVariant({ value: variant, index });
  };

  const handleEditVariantChange = (e) => {
    setEditingVariant({ ...editingVariant, value: e.target.value });
  };

  const saveVariantEdit = async () => {
    try {
      setVariantError(null);
      setVariantSuccess(null);

      if (!editingVariant.value.trim()) {
        setVariantError("Variant name cannot be empty");
        return;
      }

      // Check if new name already exists (except for the current variant)
      if (
        productVariants
          .filter((v, i) => i !== editingVariant.index)
          .includes(editingVariant.value.trim())
      ) {
        setVariantError("Another variant with this name already exists");
        return;
      }

      const updatedVariants = [...productVariants];
      updatedVariants[editingVariant.index] = editingVariant.value.trim();

      // Update in Firestore
      const variantsRef = collection(db, "ProductVariants");
      const q = query(variantsRef, where("productName", "==", selectedProduct));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const variantDoc = querySnapshot.docs[0];
        await updateDoc(variantDoc.ref, {
          variants: updatedVariants,
        });
      }

      setProductVariants(updatedVariants);
      setEditingVariant(null);
      setVariantSuccess("Variant updated successfully!");
    } catch (error) {
      console.error("Error updating variant:", error);
      setVariantError(error.message || "Failed to update variant");
    }
  };

  const confirmDeleteVariant = (variant, index) => {
    setDeletingVariant({ value: variant, index });
  };

  const deleteVariant = async () => {
    try {
      setVariantError(null);
      setVariantSuccess(null);

      const updatedVariants = productVariants.filter(
        (_, i) => i !== deletingVariant.index
      );

      // Update in Firestore
      const variantsRef = collection(db, "ProductVariants");
      const q = query(variantsRef, where("productName", "==", selectedProduct));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const variantDoc = querySnapshot.docs[0];
        await updateDoc(variantDoc.ref, {
          variants: updatedVariants,
        });
      }

      setProductVariants(updatedVariants);
      setDeletingVariant(null);
      setVariantSuccess("Variant deleted successfully!");
    } catch (error) {
      console.error("Error deleting variant:", error);
      setVariantError(error.message || "Failed to delete variant");
    }
  };

  // Renewal Settings Functions
  const fetchRenewalDays = async () => {
    try {
      const settingsRef = doc(db, "Settings", "renewalSettings");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setRenewalDays(data.renewalDays || 30);
      } else {
        // Initialize default settings if none exist
        await setDoc(settingsRef, {
          renewalDays: 30,
          createdAt: new Date().toISOString(),
          createdBy: auth.currentUser?.email || "unknown",
        });
        setRenewalDays(30);
      }
    } catch (error) {
      console.error("Error fetching renewal days:", error);
      setRenewalError("Failed to fetch renewal settings");
    }
  };

  const handleSaveRenewalDays = async () => {
    setIsLoading(true);
    setRenewalError(null);
    setRenewalSuccess(null);

    try {
      if (renewalDays < 1 || renewalDays > 90) {
        throw new Error("Please enter a number between 1 and 90 days");
      }

      const settingsRef = doc(db, "Settings", "renewalSettings");
      await setDoc(
        settingsRef,
        {
          renewalDays: parseInt(renewalDays),
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.email || "unknown",
        },
        { merge: true }
      ); // Using merge to preserve other settings

      setRenewalSuccess("Renewal notification settings updated successfully!");

      // Refresh the page content
      await fetchRenewalDays();
    } catch (error) {
      console.error("Error saving renewal days:", error);
      setRenewalError(error.message || "Failed to save renewal settings");
    } finally {
      setIsLoading(false);
    }
  };

  // Renewal Price Settings Functions
  const fetchRenewalPrices = async () => {
    try {
      const renewalPricesRef = doc(db, "Settings", "renewalPrices");
      const renewalPricesDoc = await getDoc(renewalPricesRef);

      if (renewalPricesDoc.exists()) {
        const data = renewalPricesDoc.data();
        setRenewalPrices(data.prices || {});
      } else {
        // Initialize empty renewal prices if none exist
        await setDoc(renewalPricesRef, {
          prices: {},
          createdAt: new Date().toISOString(),
          createdBy: auth.currentUser?.email || "unknown",
        });
        setRenewalPrices({});
      }
    } catch (error) {
      console.error("Error fetching renewal prices:", error);
      setRenewalPriceError("Failed to fetch renewal price settings");
    }
  };

  const handleSaveRenewalPrice = async () => {
    setRenewalPriceLoading(true);
    setRenewalPriceError(null);
    setRenewalPriceSuccess(null);

    try {
      if (!selectedRenewalProduct) {
        throw new Error("Please select a product");
      }

      if (!currentPrice) {
        throw new Error("Please enter the current price");
      }

      if (!discountedPrice) {
        throw new Error("Please enter the discounted price");
      }

      // Update the renewal prices object
      const updatedPrices = {
        ...renewalPrices,
        [selectedRenewalProduct]: {
          currentPrice: parseFloat(currentPrice),
          discountedPrice: parseFloat(discountedPrice),
          updatedAt: new Date().toISOString(),
        },
      };

      // Save to Firestore
      const renewalPricesRef = doc(db, "Settings", "renewalPrices");
      await setDoc(
        renewalPricesRef,
        {
          prices: updatedPrices,
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.email || "unknown",
        },
        { merge: true }
      );

      setRenewalPrices(updatedPrices);
      setRenewalPriceSuccess("Renewal price settings updated successfully!");
    } catch (error) {
      console.error("Error saving renewal price settings:", error);
      setRenewalPriceError(
        error.message || "Failed to save renewal price settings"
      );
    } finally {
      setRenewalPriceLoading(false);
    }
  };

  // Renewal Message Template Functions
  const fetchRenewalMessage = async () => {
    try {
      console.log("Fetching renewal message template from Firestore...");
      const messageRef = doc(db, "Settings", "renewalMessage");
      const messageDoc = await getDoc(messageRef);

      if (messageDoc.exists()) {
        const data = messageDoc.data();
        console.log("Loaded template from Firestore:", data.template);
        setRenewalMessage(data.template || "");
      } else {
        // Initialize default message template if none exists
        const defaultTemplate = `السلام عليكم 
اهلا بحضرتك 
باقي على انتهاء رخصة {productName} 
يوم {days} 
استفد من خصم التجديد المبكر {discountedPrice}
بدلا من {currentPrice}`;

        console.log(
          "Creating default renewal template in Firestore:",
          defaultTemplate
        );
        await setDoc(messageRef, {
          template: defaultTemplate,
          createdAt: new Date().toISOString(),
          createdBy: auth.currentUser?.email || "unknown",
        });
        setRenewalMessage(defaultTemplate);
      }
    } catch (error) {
      console.error("Error fetching renewal message template:", error);
      setRenewalMessageError("Failed to fetch renewal message template");
    }
  };

  const handleSaveRenewalMessage = async () => {
    setRenewalMessageLoading(true);
    setRenewalMessageError(null);
    setRenewalMessageSuccess(null);

    try {
      if (!renewalMessage.trim()) {
        throw new Error("Renewal message template cannot be empty");
      }

      // Create a timestamp for this update
      const timestamp = new Date().toISOString();
      console.log("Saving template with timestamp:", timestamp);
      console.log("New template content:", renewalMessage);

      // First, delete the document completely to ensure no old data remains
      const messageRef = doc(db, "Settings", "renewalMessage");
      try {
        await deleteDoc(messageRef);
        console.log("Previous template document deleted");
      } catch (deleteError) {
        console.log("No previous template to delete or error:", deleteError);
        // Continue even if delete fails (might not exist)
      }

      // Add a short delay to ensure deletion completes
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Create a completely new document with the template
      const cacheBuster = Date.now();
      await setDoc(messageRef, {
        template: renewalMessage,
        updatedAt: timestamp,
        updatedBy: auth.currentUser?.email || "unknown",
        forceUpdate: Math.random().toString(36).substring(2, 15), // Add a random value to force update
        version: cacheBuster, // Use timestamp as version
        cacheBuster: cacheBuster, // Add explicit cache buster field
      });

      console.log("Template saved with new document");

      // Store in localStorage as well for immediate use
      try {
        localStorage.setItem("renewal_message_template", renewalMessage);
        localStorage.setItem(
          "renewal_message_template_timestamp",
          cacheBuster.toString()
        );
        console.log("Template also saved to localStorage for immediate access");
      } catch (storageError) {
        console.warn("Could not save to localStorage:", storageError);
      }

      // Add a delay to ensure the write completes and is propagated
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Verify the save worked by reading it back with cache disabled
      const verifyDoc = await getDoc(messageRef, { source: "server" });
      if (verifyDoc.exists()) {
        console.log("Verified template after save:", verifyDoc.data().template);

        // Double check the template matches what we saved
        const savedTemplate = verifyDoc.data().template;
        if (savedTemplate !== renewalMessage) {
          console.error(
            "Template verification failed - mismatch between saved and retrieved template"
          );
          throw new Error("Template verification failed - please try again");
        }

        // Force a page reload to clear all caches
        if (
          window.confirm(
            "Template saved successfully! Reload the page to apply changes?"
          )
        ) {
          // Clear any cached data before reload
          try {
            await db.clearPersistence();
          } catch (err) {
            console.log("Could not clear persistence:", err);
          }

          window.location.reload();
        }
      }

      setRenewalMessageSuccess(
        "Renewal message template updated successfully! Please refresh the page to see changes."
      );
    } catch (error) {
      console.error("Error saving renewal message template:", error);
      setRenewalMessageError(
        error.message || "Failed to save renewal message template"
      );
    } finally {
      setRenewalMessageLoading(false);
    }
  };

  // Expire Message Template Functions
  const fetchExpireMessage = async () => {
    try {
      console.log("Fetching expire message template from Firestore...");
      setExpireMessageLoading(true);
      setExpireMessageError(null);

      // First try to get from localStorage for immediate display
      const cachedTemplate = localStorage.getItem("expire_message_template");
      const cachedTimestamp = localStorage.getItem(
        "expire_message_template_timestamp"
      );

      if (cachedTemplate && cachedTimestamp) {
        console.log("Found cached expire template:", cachedTemplate);
        setExpireMessage(cachedTemplate);
      }

      // Then fetch from Firestore
      const messageRef = doc(db, "Settings", "expireMessageTemplate");
      const messageDoc = await getDoc(messageRef, { source: "server" });

      if (messageDoc.exists()) {
        const data = messageDoc.data();
        console.log("Loaded expire template from Firestore:", data.message);

        // Update cache only if the server data is newer
        if (!cachedTimestamp || data.version > parseInt(cachedTimestamp)) {
          localStorage.setItem("expire_message_template", data.message);
          localStorage.setItem(
            "expire_message_template_timestamp",
            data.version?.toString() || Date.now().toString()
          );
        }

        setExpireMessage(data.message || "");
      } else {
        // Initialize default message template if none exists
        const defaultTemplate = `السلام عليكم 
اهلا بحضرتك 
نود اعلامك بانتهاء رخصة {productName}
السعر الحالي: {currentPrice}
سعر التجديد: {discountedPrice}`;

        console.log(
          "Creating default expire template in Firestore:",
          defaultTemplate
        );
        const timestamp = Date.now();

        await setDoc(messageRef, {
          message: defaultTemplate,
          createdAt: new Date().toISOString(),
          createdBy: auth.currentUser?.email || "unknown",
          version: timestamp,
          forceUpdate: Math.random().toString(36).substring(2, 15),
        });

        localStorage.setItem("expire_message_template", defaultTemplate);
        localStorage.setItem(
          "expire_message_template_timestamp",
          timestamp.toString()
        );

        setExpireMessage(defaultTemplate);
      }
    } catch (error) {
      console.error("Error fetching expire message template:", error);
      setExpireMessageError(
        "Failed to fetch expire message template. Please try again."
      );
    } finally {
      setExpireMessageLoading(false);
    }
  };

  // Save expire message template
  const handleSaveExpireMessage = async () => {
    try {
      setExpireMessageLoading(true);
      setExpireMessageError(null);
      setExpireMessageSuccess(null);

      // Create a timestamp for this update
      const timestamp = Date.now();
      console.log("Saving expire template with timestamp:", timestamp);
      console.log("New expire template content:", expireMessage);

      // First, delete the document completely to ensure no old data remains
      const messageRef = doc(db, "Settings", "expireMessageTemplate");
      try {
        await deleteDoc(messageRef);
        console.log("Previous expire template document deleted");
      } catch (deleteError) {
        console.log(
          "No previous expire template to delete or error:",
          deleteError
        );
      }

      // Add a short delay to ensure deletion completes
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Create a completely new document with the template
      await setDoc(messageRef, {
        message: expireMessage,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || "unknown",
        forceUpdate: Math.random().toString(36).substring(2, 15),
        version: timestamp,
        cacheBuster: timestamp,
      });

      console.log("Expire template saved with new document");

      // Store in localStorage for immediate use
      localStorage.setItem("expire_message_template", expireMessage);
      localStorage.setItem(
        "expire_message_template_timestamp",
        timestamp.toString()
      );
      console.log(
        "Expire template also saved to localStorage for immediate access"
      );

      // Add a delay to ensure the write completes and is propagated
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Verify the save worked by reading it back
      const verifyDoc = await getDoc(messageRef, { source: "server" });
      if (verifyDoc.exists()) {
        console.log(
          "Verified expire template after save:",
          verifyDoc.data().message
        );

        // Double check the template matches what we saved
        const savedTemplate = verifyDoc.data().message;
        if (savedTemplate !== expireMessage) {
          console.error(
            "Expire template verification failed - mismatch between saved and retrieved template"
          );
          throw new Error("Template verification failed - please try again");
        }

        setExpireMessageSuccess("Expire message template saved successfully!");

        // Force a page reload to clear all caches
        if (
          window.confirm(
            "Template saved successfully! Reload the page to apply changes?"
          )
        ) {
          // Clear any cached data before reload
          try {
            await db.clearPersistence();
          } catch (err) {
            console.log("Could not clear persistence:", err);
          }
          window.location.reload();
        }
      }
    } catch (error) {
      console.error("Error saving expire message template:", error);
      setExpireMessageError(
        "Failed to save expire message template. Please try again."
      );
    } finally {
      setExpireMessageLoading(false);
    }
  };

  // Messaging Settings Functions
  const fetchMessagingSettings = async () => {
    try {
      const settingsRef = doc(db, "Settings", "messagingSettings");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setFacebookBusinessUrl(data.facebookBusinessUrl || "");
      }
    } catch (error) {
      console.error("Error fetching messaging settings:", error);
      setMessagingSettingsError("Failed to fetch messaging settings");
    }
  };

  const handleSaveMessagingSettings = async () => {
    setMessagingSettingsLoading(true);
    setMessagingSettingsError(null);
    setMessagingSettingsSuccess(null);

    try {
      const settingsRef = doc(db, "Settings", "messagingSettings");
      await setDoc(
        settingsRef,
        {
          facebookBusinessUrl: facebookBusinessUrl,
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.email || "unknown",
        },
        { merge: true }
      );

      setMessagingSettingsSuccess("Messaging settings updated successfully!");
    } catch (error) {
      console.error("Error saving messaging settings:", error);
      setMessagingSettingsError(
        error.message || "Failed to save messaging settings"
      );
    } finally {
      setMessagingSettingsLoading(false);
    }
  };

  // Two-Factor Authentication Functions
  const fetchTwoFactorStatus = async () => {
    setTwoFactorLoading(true);
    setTwoFactorError(null);

    try {
      const user = auth.currentUser;

      // Log authentication status for debugging
      console.log(
        "Current authentication state:",
        user ? "User authenticated" : "No user authenticated"
      );

      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          console.log("Fetching 2FA status for user:", user.uid);

          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log("User document found:", userData);
            setTwoFactorEnabled(userData.twoFactorEnabled || false);
          } else {
            console.log("User document doesn't exist, creating a new one");
            // Create user document if it doesn't exist
            await setDoc(userRef, {
              email: user.email,
              twoFactorEnabled: false,
              createdAt: new Date().toISOString(),
            });
            setTwoFactorEnabled(false);
          }
        } catch (firestoreError) {
          console.error("Firestore error:", firestoreError);
          setTwoFactorError(
            `Database error: ${firestoreError.code || firestoreError.message}`
          );
        }
      } else {
        console.error("No user is authenticated");
        setTwoFactorError(
          "You must be logged in to manage two-factor authentication"
        );

        // Try to re-authenticate the user
        try {
          await ensureAuthenticated();
          console.log("Attempted to ensure authentication");
        } catch (authError) {
          console.error("Failed to ensure authentication:", authError);
        }
      }
    } catch (error) {
      console.error("Error fetching 2FA status: ", error);
      setTwoFactorError(
        `Failed to fetch two-factor authentication status: ${error.message}`
      );
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    setTwoFactorLoading(true);
    setTwoFactorError(null);
    setTwoFactorSuccess(null);

    try {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, {
          twoFactorEnabled: false,
        });

        setTwoFactorEnabled(false);
        setTwoFactorSuccess("Two-factor authentication has been disabled.");
      }
    } catch (error) {
      console.error("Error disabling 2FA: ", error);
      setTwoFactorError("Failed to disable two-factor authentication.");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleTwoFactorSetupComplete = () => {
    setShowTwoFactorSetup(false);
    setTwoFactorEnabled(true);
    setTwoFactorSuccess(
      "Two-factor authentication has been enabled successfully."
    );
    fetchTwoFactorStatus();
  };

  // Fetch global 2FA requirement status
  const fetchGlobalTwoFactorStatus = async () => {
    try {
      console.log("Fetching global 2FA status");
      const required = await isGlobalTwoFactorRequired();
      console.log("Global 2FA status fetched:", required);
      setGlobalTwoFactorRequiredState(required);
      setCanManageGlobalSettings(true);
    } catch (error) {
      console.error("Error fetching global 2FA status:", error);

      // If there's a permission error, assume user can't manage global settings
      if (error.code === "permission-denied") {
        setCanManageGlobalSettings(false);
        console.log(
          "User doesn't have permission to manage global 2FA settings"
        );
      } else {
        setTwoFactorError(
          `Failed to fetch global two-factor settings: ${error.message}`
        );
      }
    }
  };

  // Toggle global 2FA requirement
  const toggleGlobalTwoFactorRequired = async () => {
    setTwoFactorLoading(true);
    setTwoFactorError(null);
    setTwoFactorSuccess(null);

    try {
      const newStatus = !globalTwoFactorRequired;
      const result = await setGlobalTwoFactorRequiredAPI(newStatus);

      if (result === true) {
        setGlobalTwoFactorRequiredState(newStatus);
        setTwoFactorSuccess(
          newStatus
            ? "Two-factor authentication is now required for all users"
            : "Two-factor authentication is now optional for users"
        );
      } else {
        throw new Error("Failed to update global settings");
      }
    } catch (error) {
      console.error("Error toggling global 2FA requirement:", error);

      // Provide more specific error message for permission issues
      if (error.code === "permission-denied") {
        setTwoFactorError(
          "You don't have permission to change global 2FA settings. Please contact an administrator."
        );
      } else {
        setTwoFactorError(
          "Failed to update global two-factor authentication settings. " +
            error.message
        );
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const styles = {
    container: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "20px",
    },
    section: {
      backgroundColor: "white",
      padding: "24px",
      borderRadius: "8px",
      marginBottom: "24px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    },
    sectionHeader: {
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: "#1e293b",
    },
    form: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr auto",
      gap: "20px",
      marginBottom: "24px",
    },
    input: {
      padding: "10px",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      fontSize: "14px",
      width: "100%",
    },
    button: {
      padding: "10px 20px",
      cursor: "pointer",
      border: "none",
      borderRadius: "6px",
      backgroundColor: "#3b82f6",
      color: "white",
      fontWeight: "500",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "background-color 0.2s",
      "&:hover": {
        backgroundColor: "#2563eb",
      },
    },
    productRow: {
      display: "grid",
      gridTemplateColumns: "60px 1fr 1fr 100px 100px",
      gap: "20px",
      alignItems: "center",
      padding: "12px 0",
      borderBottom: "1px solid #e2e8f0",
    },
    productImage: {
      width: "40px",
      height: "40px",
      objectFit: "contain",
      borderRadius: "4px",
    },
    deleteButton: {
      backgroundColor: "#ef4444",
      "&:hover": {
        backgroundColor: "#dc2626",
      },
    },
    modal: {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "white",
      padding: "24px",
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      zIndex: 1000,
    },
    backdrop: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 999,
    },
    settingsForm: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    error: {
      color: "#dc2626",
      backgroundColor: "#fee2e2",
      padding: "12px",
      borderRadius: "6px",
      marginBottom: "16px",
    },
    success: {
      color: "#059669",
      backgroundColor: "#d1fae5",
      padding: "12px",
      borderRadius: "6px",
      marginBottom: "16px",
    },
  };

  return (
    <div style={styles.container}>
      {/* Product Management Section */}
      <div style={styles.section}>
        <h2
          style={{ ...styles.sectionHeader, cursor: 'pointer', marginBottom: 0, justifyContent: 'space-between' }}
          onClick={() => toggleSection('products')}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Package size={24} />
            Product Management
          </span>
          {openSections.products ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
        </h2>
        {openSections.products && (<>

        {error && (
          <div style={styles.error}>
            <AlertCircle
              size={16}
              style={{ display: "inline", marginRight: "8px" }}
            />
            {error}
          </div>
        )}

        <form onSubmit={addProduct} style={styles.form}>
          <input
            type="text"
            name="name"
            value={newProduct.name}
            onChange={handleInputChange}
            placeholder="Product Name"
            required
            style={styles.input}
          />
          <input
            type="number"
            name="price"
            value={newProduct.price}
            onChange={handleInputChange}
            placeholder="Price"
            required
            style={styles.input}
          />
          <input
            type="url"
            name="logo"
            value={newProduct.logo}
            onChange={handleInputChange}
            placeholder="Logo URL"
            required
            style={styles.input}
          />
          <button type="submit" style={styles.button}>
            Add Product
          </button>
        </form>

        <div style={styles.productRow}>
          <span>Logo</span>
          <span>Name</span>
          <span>Price</span>
          <span>Edit</span>
          <span>Delete</span>
        </div>

        {products.map((product) => (
          <div key={product.id} style={styles.productRow}>
            {editingProduct && editingProduct.id === product.id ? (
              <>
                <input
                  type="url"
                  name="logo"
                  value={editingProduct.logo}
                  onChange={(e) => handleInputChange(e, false)}
                  style={styles.input}
                />
                <input
                  type="text"
                  name="name"
                  value={editingProduct.name}
                  onChange={(e) => handleInputChange(e, false)}
                  style={styles.input}
                />
                <input
                  type="number"
                  name="price"
                  value={editingProduct.price}
                  onChange={(e) => handleInputChange(e, false)}
                  style={styles.input}
                />
                <button onClick={saveEdit} style={styles.button}>
                  <Edit2 size={16} />
                  Save
                </button>
                <button
                  onClick={() => setEditingProduct(null)}
                  style={{ ...styles.button, backgroundColor: "#64748b" }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <img
                  src={product.logo}
                  alt={product.name}
                  style={styles.productImage}
                />
                <span>{product.name}</span>
                <span>${product.price}</span>
                <button
                  onClick={() => startEditing(product)}
                  style={styles.button}
                >
                  <Edit2 size={16} />
                  Edit
                </button>
                <button
                  onClick={() => confirmDelete(product)}
                  style={{ ...styles.button, ...styles.deleteButton }}
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </>
            )}
          </div>
        ))}
        </>)}
      </div>

      {/* Product Variants Section */}
      <div style={styles.section}>
        <h2
          style={{ ...styles.sectionHeader, cursor: 'pointer', marginBottom: 0, justifyContent: 'space-between' }}
          onClick={() => toggleSection('variants')}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <List size={24} />
            Product Variants Management
          </span>
          {openSections.variants ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
        </h2>
        {openSections.variants && (<>

        {variantError && (
          <div style={styles.error}>
            <AlertCircle
              size={16}
              style={{ display: "inline", marginRight: "8px" }}
            />
            {variantError}
          </div>
        )}

        {variantSuccess && <div style={styles.success}>{variantSuccess}</div>}

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{ display: "block", marginBottom: "6px", fontWeight: "500" }}
          >
            Select Product:
          </label>
          <select
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            style={{
              padding: "10px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              width: "100%",
              marginBottom: "16px",
            }}
          >
            {products.map((product) => (
              <option key={product.id} value={product.name}>
                {product.name}
              </option>
            ))}
          </select>

          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "20px",
            }}
          >
            <input
              type="text"
              value={newVariant}
              onChange={handleNewVariantChange}
              placeholder="Enter new variant name"
              style={styles.input}
            />
            <button onClick={addVariant} style={styles.button}>
              <Plus size={16} />
              Add Variant
            </button>
          </div>

          {productVariants.length > 0 ? (
            <div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "12px",
                  color: "#475569",
                }}
              >
                {selectedProduct} Variants:
              </h3>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  overflow: "hidden",
                }}
              >
                {productVariants.map((variant, index) => (
                  <li
                    key={index}
                    style={{
                      padding: "12px 16px",
                      borderBottom:
                        index < productVariants.length - 1
                          ? "1px solid #e2e8f0"
                          : "none",
                      backgroundColor: index % 2 === 0 ? "#f8fafc" : "white",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {editingVariant && editingVariant.index === index ? (
                      <div
                        style={{ display: "flex", gap: "8px", width: "100%" }}
                      >
                        <input
                          type="text"
                          value={editingVariant.value}
                          onChange={handleEditVariantChange}
                          style={styles.input}
                          autoFocus
                        />
                        <button onClick={saveVariantEdit} style={styles.button}>
                          Save
                        </button>
                        <button
                          onClick={() => setEditingVariant(null)}
                          style={{
                            ...styles.button,
                            backgroundColor: "#64748b",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <span>{variant}</span>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            onClick={() => startEditingVariant(variant, index)}
                            style={{ ...styles.button, padding: "6px 10px" }}
                          >
                            <Edit2 size={14} />
                            Edit
                          </button>
                          <button
                            onClick={() => confirmDeleteVariant(variant, index)}
                            style={{
                              ...styles.button,
                              ...styles.deleteButton,
                              padding: "6px 10px",
                            }}
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div
              style={{
                padding: "16px",
                backgroundColor: "#f8fafc",
                borderRadius: "6px",
                textAlign: "center",
                color: "#64748b",
              }}
            >
              No variants defined for {selectedProduct}. Add your first variant
              above.
            </div>
          )}
        </div>
        </>)}
      </div>

      {/* Renewal Settings Section */}
      <div style={styles.section}>
        <h2
          style={{ ...styles.sectionHeader, cursor: 'pointer', marginBottom: 0, justifyContent: 'space-between' }}
          onClick={() => toggleSection('renewal')}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BellRing size={24} />
            Renewal Notification Settings
          </span>
          {openSections.renewal ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
        </h2>
        {openSections.renewal && (<>

        {renewalError && (
          <div style={styles.error}>
            <AlertCircle
              size={16}
              style={{ display: "inline", marginRight: "8px" }}
            />
            {renewalError}
          </div>
        )}

        {renewalSuccess && <div style={styles.success}>{renewalSuccess}</div>}

        <div style={styles.settingsForm}>
          <div>
            <label style={{ marginBottom: "8px", display: "block" }}>
              Notify before expiration (days)
            </label>
            <input
              type="number"
              min="1"
              max="90"
              value={renewalDays}
              onChange={(e) => setRenewalDays(e.target.value)}
              style={styles.input}
            />
          </div>

          <button
            onClick={handleSaveRenewalDays}
            disabled={isLoading}
            style={styles.button}
          >
            {isLoading ? "Saving..." : "Save Settings"}
          </button>
        </div>
        </>)}
      </div>

      {/* Renewal Price Settings Section */}
      <div style={styles.section}>
        <h2
          style={{ ...styles.sectionHeader, cursor: 'pointer', marginBottom: 0, justifyContent: 'space-between' }}
          onClick={() => toggleSection('renewalPrice')}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag size={24} />
            Renewal Price Settings
          </span>
          {openSections.renewalPrice ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
        </h2>
        {openSections.renewalPrice && (<>

        {renewalPriceError && (
          <div style={styles.error}>
            <AlertCircle
              size={16}
              style={{ display: "inline", marginRight: "8px" }}
            />
            {renewalPriceError}
          </div>
        )}

        {renewalPriceSuccess && (
          <div style={styles.success}>{renewalPriceSuccess}</div>
        )}

        <div style={styles.settingsForm}>
          <div>
            <label style={{ marginBottom: "8px", display: "block" }}>
              Select Product
            </label>
            <select
              value={selectedRenewalProduct}
              onChange={(e) => setSelectedRenewalProduct(e.target.value)}
              style={{
                padding: "10px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                width: "100%",
                marginBottom: "16px",
              }}
            >
              {products.map((product) => (
                <option key={product.id} value={product.name}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ marginBottom: "8px", display: "block" }}>
              Current Price (Regular)
            </label>
            <input
              type="number"
              value={currentPrice}
              onChange={(e) => setCurrentPrice(e.target.value)}
              placeholder="Enter current price"
              style={styles.input}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ marginBottom: "8px", display: "block" }}>
              Discounted Price (For Renewal)
            </label>
            <input
              type="number"
              value={discountedPrice}
              onChange={(e) => setDiscountedPrice(e.target.value)}
              placeholder="Enter discounted price"
              style={styles.input}
            />
          </div>

          <button
            onClick={handleSaveRenewalPrice}
            disabled={renewalPriceLoading}
            style={styles.button}
          >
            {renewalPriceLoading ? "Saving..." : "Save Price Settings"}
          </button>
        </div>
        </>)}
      </div>

      {/* Messaging Settings Section */}
      <div style={styles.section}>
        <h2
          style={{ ...styles.sectionHeader, cursor: 'pointer', marginBottom: 0, justifyContent: 'space-between' }}
          onClick={() => toggleSection('messaging')}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageSquare size={24} />
            Messaging Settings
          </span>
          {openSections.messaging ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
        </h2>
        {openSections.messaging && (<>

        <div style={styles.settingsForm}>
          <div>
            <label style={{ marginBottom: "8px", display: "block" }}>
              Facebook Business URL
            </label>
            <input
              type="text"
              value={facebookBusinessUrl || ""}
              onChange={(e) => setFacebookBusinessUrl(e.target.value)}
              placeholder="https://business.facebook.com/latest/inbox/..."
              style={{ ...styles.input, width: "100%" }}
            />
            <p style={{ fontSize: "13px", color: "#64748b", marginTop: "6px" }}>
              Enter your Facebook Business inbox URL to use for messaging
            </p>
          </div>

          <button
            onClick={handleSaveMessagingSettings}
            disabled={messagingSettingsLoading}
            style={styles.button}
          >
            {messagingSettingsLoading ? "Saving..." : "Save Messaging Settings"}
          </button>
        </div>
        </>)}
      </div>

      {/* Renewal Message Template Section */}
      <div style={styles.section}>
        <h2
          style={{ ...styles.sectionHeader, cursor: 'pointer', marginBottom: 0, justifyContent: 'space-between' }}
          onClick={() => toggleSection('renewalMsg')}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageSquare size={24} />
            Renewal Message Template
          </span>
          {openSections.renewalMsg ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
        </h2>
        {openSections.renewalMsg && (<>

        {renewalMessageError && (
          <div style={styles.error}>
            <AlertCircle
              size={16}
              style={{ display: "inline", marginRight: "8px" }}
            />
            {renewalMessageError}
          </div>
        )}

        {renewalMessageSuccess && (
          <div style={styles.success}>{renewalMessageSuccess}</div>
        )}

        <div style={styles.settingsForm}>
          <div>
            <label style={{ marginBottom: "8px", display: "block" }}>
              Renewal Message Template
            </label>
            <div style={{ marginBottom: "16px" }}>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  marginBottom: "12px",
                }}
              >
                You can use the following variables in your message:
              </p>
              <ul
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  paddingLeft: "20px",
                  marginBottom: "12px",
                }}
              >
                <li>
                  <code>{"{productName}"}</code> - The name of the product
                </li>
                <li>
                  <code>{"{days}"}</code> - Days until expiration
                </li>
                <li>
                  <code>{"{currentPrice}"}</code> - Regular price
                </li>
                <li>
                  <code>{"{discountedPrice}"}</code> - Discounted renewal price
                </li>
                <li>
                  <code>{"{clientEmail}"}</code> - Client email
                </li>
                <li>
                  <code>{"{clientName}"}</code> - Client name
                </li>
              </ul>
            </div>
            <textarea
              value={renewalMessage}
              onChange={(e) => setRenewalMessage(e.target.value)}
              placeholder="Enter renewal message template"
              style={{
                ...styles.input,
                minHeight: "200px",
                fontFamily: "inherit",
                direction: "rtl", // For Arabic text
                textAlign: "right",
              }}
            />
          </div>

          <button
            onClick={handleSaveRenewalMessage}
            disabled={renewalMessageLoading}
            style={styles.button}
          >
            {renewalMessageLoading ? "Saving..." : "Save Message Template"}
          </button>
        </div>
        </>)}
      </div>

      {/* Expire Message Template Section */}
      <div style={styles.section}>
        <h2
          style={{ ...styles.sectionHeader, cursor: 'pointer', marginBottom: 0, justifyContent: 'space-between' }}
          onClick={() => toggleSection('expireMsg')}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertCircle size={24} />
            Expire Message Template
          </span>
          {openSections.expireMsg ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
        </h2>
        {openSections.expireMsg && (<>

        {expireMessageError && (
          <div style={styles.error}>
            <AlertCircle
              size={16}
              style={{ display: "inline", marginRight: "8px" }}
            />
            {expireMessageError}
          </div>
        )}

        {expireMessageSuccess && (
          <div style={styles.success}>{expireMessageSuccess}</div>
        )}

        <div style={styles.settingsForm}>
          <div>
            <label style={{ marginBottom: "8px", display: "block" }}>
              Expire Message Template
            </label>
            <div style={{ marginBottom: "16px" }}>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  marginBottom: "12px",
                }}
              >
                You can use the following variables in your message:
              </p>
              <ul
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  paddingLeft: "20px",
                  marginBottom: "12px",
                }}
              >
                <li>
                  <code>{"{productName}"}</code> - The name of the product
                </li>
                <li>
                  <code>{"{currentPrice}"}</code> - Regular price
                </li>
                <li>
                  <code>{"{discountedPrice}"}</code> - Discounted renewal price
                </li>
                <li>
                  <code>{"{clientEmail}"}</code> - Client email
                </li>
                <li>
                  <code>{"{clientName}"}</code> - Client name
                </li>
              </ul>
            </div>
            <textarea
              value={expireMessage}
              onChange={(e) => setExpireMessage(e.target.value)}
              placeholder="Enter expire message template"
              style={{
                ...styles.input,
                minHeight: "200px",
                fontFamily: "inherit",
                direction: "rtl", // For Arabic text
                textAlign: "right",
              }}
            />
          </div>

          <button
            onClick={handleSaveExpireMessage}
            disabled={expireMessageLoading}
            style={styles.button}
          >
            {expireMessageLoading ? "Saving..." : "Save Message Template"}
          </button>
        </div>
        </>)}
      </div>

      {/* Two-Factor Authentication Section */}
      <div style={styles.section}>
        <h2
          style={{ ...styles.sectionHeader, cursor: 'pointer', marginBottom: 0, justifyContent: 'space-between' }}
          onClick={() => toggleSection('twoFactor')}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Shield size={24} />
            Two-Factor Authentication
          </span>
          {openSections.twoFactor ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
        </h2>
        {openSections.twoFactor && (<>

        <div style={styles.settingsForm}>
          {twoFactorLoading ? (
            <p>Loading...</p>
          ) : showTwoFactorSetup ? (
            <TwoFactorAuth
              setupMode={true}
              onVerified={handleTwoFactorSetupComplete}
              userEmail={auth.currentUser?.email}
            />
          ) : (
            <div className="two-factor-settings">
              <p>
                Two-factor authentication adds an extra layer of security to
                your account. When enabled, you'll need to enter a verification
                code from Google Authenticator app in addition to your password
                when signing in.
              </p>

              {/* Global 2FA Setting (admin only) */}
              {canManageGlobalSettings && (
                <div className="two-factor-global-setting">
                  <h3>Global Settings</h3>
                  <p>
                    As an administrator, you can require two-factor
                    authentication for all users of the system. When enabled,
                    users will be prompted to set up 2FA when they sign in.
                  </p>
                  <div className="two-factor-status">
                    <p>
                      <strong>Require 2FA for all users:</strong>{" "}
                      {globalTwoFactorRequired ? "Enabled" : "Disabled"}
                    </p>
                    <button
                      className={
                        globalTwoFactorRequired
                          ? "danger-button"
                          : "primary-button"
                      }
                      onClick={toggleGlobalTwoFactorRequired}
                      disabled={twoFactorLoading}
                    >
                      {globalTwoFactorRequired
                        ? "Make 2FA Optional"
                        : "Require 2FA for All Users"}
                    </button>
                  </div>
                </div>
              )}

              <h3>Your Account Settings</h3>
              <div className="two-factor-status">
                <p>
                  <strong>Your 2FA Status:</strong>{" "}
                  {twoFactorEnabled ? "Enabled" : "Disabled"}
                </p>

                {twoFactorEnabled ? (
                  <button
                    className="danger-button"
                    onClick={handleDisableTwoFactor}
                    disabled={twoFactorLoading}
                  >
                    Disable Two-Factor Authentication
                  </button>
                ) : (
                  <button
                    className="primary-button"
                    onClick={() => setShowTwoFactorSetup(true)}
                    disabled={twoFactorLoading}
                  >
                    Enable Two-Factor Authentication
                  </button>
                )}
              </div>

              {twoFactorSuccess && (
                <p className="success-message">{twoFactorSuccess}</p>
              )}
              {twoFactorError && (
                <p className="error-message">{twoFactorError}</p>
              )}
            </div>
          )}
        </div>
        </>)}
      </div>

      {/* Delete Product Confirmation Modal */}
      {deletingProduct && (
        <>
          <div
            style={styles.backdrop}
            onClick={() => setDeletingProduct(null)}
          />
          <div style={styles.modal}>
            <p style={{ marginBottom: "20px" }}>
              Are you sure you want to delete {deletingProduct.name}?
            </p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={deleteProduct}
                style={{ ...styles.button, ...styles.deleteButton }}
              >
                <Trash2 size={16} />
                Confirm Delete
              </button>
              <button
                onClick={() => setDeletingProduct(null)}
                style={{ ...styles.button, backgroundColor: "#64748b" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Variant Confirmation Modal */}
      {deletingVariant && (
        <>
          <div
            style={styles.backdrop}
            onClick={() => setDeletingVariant(null)}
          />
          <div style={styles.modal}>
            <p style={{ marginBottom: "20px" }}>
              Are you sure you want to delete the variant "
              {deletingVariant.value}"?
            </p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={deleteVariant}
                style={{ ...styles.button, ...styles.deleteButton }}
              >
                <Trash2 size={16} />
                Confirm Delete
              </button>
              <button
                onClick={() => setDeletingVariant(null)}
                style={{ ...styles.button, backgroundColor: "#64748b" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Settings;
